import { useDashboardFilters } from "@/dashboard/store/useDashboardFilters";
import { useCallback, useMemo } from "react";
import { useTranslation } from "react-i18next";
import { LocationSelect } from "../location-select";
import { City } from "./types";

interface CityInputProps {
  location: string;
  data: City[];
  isLoading: boolean;
  selectedSpaces: { id: string; name: string; city: string }[] | null;
  onSubmit?: boolean;
  onChange?: (value: City[] | null) => void;
}

/**
 * Cidades da modal "Mais filtros" do Painel. Os espaços escolhidos limitam as
 * cidades listadas; com `onSubmit`, a seleção só vai ao store em "Filtrar".
 */
export function CityInput({
  location,
  data,
  isLoading,
  selectedSpaces,
  onSubmit,
  onChange,
}: CityInputProps) {
  const { t } = useTranslation(["dashboard"]);
  const { filters, setFilters } = useDashboardFilters();

  const handleChange = useCallback(
    (value: City[] | null) => {
      if (onSubmit) {
        onChange?.(value);
      } else {
        setFilters({ cities: value as City[] });
      }
    },
    [onChange, onSubmit, setFilters],
  );

  const options = useMemo(
    () =>
      selectedSpaces && selectedSpaces.length > 0
        ? data.filter((city) =>
            selectedSpaces.some((space) => space.city === city.id),
          )
        : data,
    [selectedSpaces, data],
  );

  return (
    <LocationSelect<City>
      id={`${location}-city-filter`}
      label={t("city-filter-label") as string}
      placeholder={t("city-filter-placeholder") as string}
      searchPlaceholder={t("city-filter-search-placeholder") as string}
      emptyText={t("city-filter-empty-state") as string}
      multipleLabel={(count) =>
        t("city-filter-selected-multiple", { count }) as string
      }
      options={options}
      value={filters.cities}
      isLoading={isLoading}
      onChange={handleChange}
    />
  );
}
